// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Polygon edge class implementation.
 * @author James Reid
 */

// @ts-check

// @@imports-module
import { Vector2D } from "./Vector.js"

// @@imports-utils
import { DecoratedError } from "../../logging/index.js"

// @@body
/**
 * Class implementing edge from start and end vectors providing:
 * - Start vertex of edge.
 * - Edge vector reflecting magnitude and direction of edge.
 * - *Dynamically* generated end vertex of edge based on start vertex and edge
 * vector such that if edges are connected end to end as expected, the end
 * vertex of an edge will *not* point to the start vertex of the next edge,
 * except by equal value (i.e. searching for an edge with a start vertex with
 * identical coordinates). This isolates each edge, and prevents potentially
 * undesired run-on updates to consecutively linked edge instances.
 * - Intersect method to check edge intersection with other edges or vertex
 * vectors.
 */
class Edge extends Vector2D {
    /** @type {Vector2D} */ #start

    /**
     * Construct edge from start and end vectors, creating an edge class which
     * is represented by a start vector (equivalent to start vertex) and an edge
     * vector.
     * @param {Vector2D} start Start vector (equivalent to vertex) of edge.
     * @param {Vector2D} end End vector (equivalent to vertex) of edge.
     */
    constructor(start, end) {
        // Calculate end vector from start and end vertex vectors.
        const { x, y } = end.subtract(start)
        super(x, y)

        this.#start = start
    }

    /**
     * Update edge given optional updated `start` and `end` vectors of edge, and
     * optional `x` and `y` coordinates of edge vector.
     * @param {object} obj Optional destructured parameters object.
     * @param {Vector2D} [obj.start] Updated `start` vector of edge. Note that:
     * - This vector will *not* be assigned to the current edge instance.
     * - The `x` and `y` coordinate values of this vector will be used to update
     * the internal `start` vector of the current edge index.
     * - The object reference of the internal `start` vector will *not* change.
     * @param {Vector2D} [obj.end] Updated `end` vector of edge. Note that:
     * - This vector will *not* be assigned to the current edge instance.
     * - This vector will be used to update the `start` vector based on the
     * direction and magnitude of the edge vector.
     * @param {number} [obj.x] New coordinate `x` of edge vector.
     * @param {number} [obj.y] New coordinate `y` of edge vector.
     * @returns {Edge} Current edge instance with required properties updated.
     */
    set({ start, end, x, y } = {}) {
        if (start) { this.#start.set(start) }
        if (end) { this.#start.set(end.subtract(this)) }
        if (typeof x === "number" || typeof y === "number") {
            super.set({ x, y })
        }

        return this
    }

    /**
     * Rotate current edge instance by an angle in *radians* around an origin
     * point including start vertex, end vertex and edge vector. By default
     * performs operation on new instance.
     * @param {*} angle Angle of rotation in *radians*.
     * @param {object} obj Optional destructured parameters object.
     * @param {Vector2D} [obj.origin] Origin of rotation, defaults to `(0,0)`.
     * @param {boolean} [obj.inPlace] Should method update current instance of
     * edge *in place*, or should method return a new edge instance?
     * @returns {Edge} Edge result of rotation.
     */
    rotate(angle, { origin = new Vector2D(0, 0), inPlace = false } = {}) {
        const { start, end } = {
            start: this.#start.rotate(angle, { origin }),
            end: this.end.rotate(angle, { origin }),
        }
        const { x, y } = end.subtract(start)

        // Only set internal start vertex and `x`, `y` coordinates of edge
        // vector. Do *not* set dynamic end vertex, as this will reflect to a
        // second, incorrect update of the start vertex.
        if (inPlace) { this.set({ start, x, y }) }

        return inPlace ? this : new Edge(start, end)
    }

    /**
     * Evaluate if a given edge is equal (x and y coordinates *and* start vector
     * are the same) to the current edge instance.
     * @param {Edge} edge Edge to compare to current instance.
     * @returns {boolean} Is edge argument equal to current edge instance?
     */
    equals(edge) {
        return super.equals(edge) && this.#start.equals(edge.start)
    }

    /**
     * Copy current edge instance to a new edge, copying the start vertex object
     * such that no object references are shared/duplicated between the two
     * instances.
     * @param {Vector2D} start Optional start vertex to be used in copy. This
     * can be used to maintain the required references to edge start vertices.
     * Defaults to a copy of the start vertex of the current instance.
     * @returns {Edge} New edge instance copied from current instance.
     */
    copy(start = this.#start.copy()) {
        return new Edge(start, this.end)
    }

    /**
     * Check if current edge instance intersects a given edge or vector target.
     * @param {Vector2D|Edge} target Edge or vector target to check for
     * intersection with current edge instance.
     * @returns {boolean} Does current edge instance intersect edge or vector
     * target?
     */
    intersects(target) {
        return target instanceof Edge
            ? this.#intersectsEdge(target)
            : this.#intersectsVector(target)
    }

    /**
     * Calculate vector point of intersection between the current edge instance
     * and another edge instance.
     * @param {Edge} edge Edge to find intersection with current instance.
     * @returns {?Vector2D} Vector of intersection between two edges. If edges
     * do not intersect, method will return `null` value.
     */
    getIntersection(edge) {
        // Ignore if there is no *single* intersection point (i.e. if the edges
        // are parallel, or if the line segments do not intersect).
        if (!this.intersects(edge) || this.isParallelTo(edge)) { return null }

        // Calculate `x` coordinate of intersection based on equating straight
        // line graph functions of each edge and solving for `x`. If any
        // gradient is non-finite, fallback to `x` intercept of that edge.
        const deltaIntercept = edge.yIntercept.y - this.yIntercept.y
        const deltaGradient = this.gradient - edge.gradient
        const x = !isFinite(this.gradient) ? this.xIntercept.x
            : !isFinite(edge.gradient) ? edge.xIntercept.x
            : deltaIntercept / deltaGradient

        // Calculate `y` coordinate of intersection based on straight line
        // graph equation `y = mx + c`.
        const y = this.gradient === 0 ? this.yIntercept.y
            : isFinite(this.gradient) ? this.gradient * x + this.yIntercept.y
            : edge.gradient * x + edge.yIntercept.y

        return new Vector2D(x, y)
    }

    /**
     * Calculate point of intersection between the current edge instance and
     * another edge instance as a fraction along current edge from start vertex.
     * @param {Edge} edge Edge to find intersection with current instance.
     * @returns {?number} Intersection point between two edges as given by a
     * decimal between 0 and 1 representing a point which is a fraction along
     * current edge from start vertex. If edges do not intersect, method will
     * return `null` value.
     */
    getIntersectionFraction(edge) {
        const intersection = this.getIntersection(edge)
        if (!intersection) { return null }

        return intersection.subtract(this.#start).magnitude / this.magnitude
    }

    /**
     * Calculate start and end intersection vertices of the overlapping section
     * between two *parallel* and *colinear* edges.
     * @param {Edge} edge Parallel edge to find intersection points with current
     * edge instance.
     * @returns {?{start:Vector2D, end:Vector2D}} Object containing start and
     * end vector points of intersection between two edges. Note that both
     * points will be provided regardless of if they intersect the current edge
     * instance, or if they only intersect the line of the extended edge.
     */
    getParallelIntersections(edge) {
        // Ignore if there is only a single intersection point (i.e. if edges
        // are not parallel), or if edges are *parallel* but not *colinear*
        // (i.e. if edges do not lie along the same line and therefore never
        // intersect).
        if (!this.isParallelTo(edge)) { return null }
        if (
            !this.yIntercept.equals(edge.yIntercept) &&
            !this.xIntercept.equals(edge.xIntercept)
        ) { return null }

        // Swap start and end vertices of edge parameter as required to match
        // direction of *current* edge instance such that the start intersect
        // will always come *before* the end intersect when traversing current
        // edge instance from the start vertex.
        const { start, end } = edge.direction !== this.direction
            ? { start: edge.end, end: edge.start }
            : edge

        return { start, end }
    }

    /**
     * Calculate start and end intersection points of the overlapping section
     * between two *parallel* and *colinear* edges as fractions along current
     * edge from start vertex.
     * @param {Edge} edge Parallel edge to find fractional intersection points
     * with current edge instance.
     * @returns {?{start:number, end:number}} Object containing start and end
     * intersection points as given by decimals between 0 and 1 representing
     * points which are a fraction along current edge from start vertex.
     * If edges do not intersect, method will return `null` value.
     */
    getParallelIntersectionFractions(edge) {
        // Get start and end parallel intersection vertices, and ignore if null.
        const parallelIntersections = this.getParallelIntersections(edge)
        if (!parallelIntersections) { return null }
        const { start, end } = parallelIntersections

        // Get edges *from* current edge start vertex *to* the start and end
        // vertices of the give edge parameter.
        const startEdge = new Edge(this.start, start)
        const endEdge = new Edge(this.start, end)

        // Start and end factors determine direction of start or end intersect
        // fraction (i.e. `+ 1` indicates +ve intersection *along* current edge,
        // and `- 1` indicates -ve intersection *before* start vertex of current
        // edge).
        const startFactor = startEdge.direction === this.direction ? 1 : - 1
        const endFactor = endEdge.direction === this.direction ? 1 : - 1

        // Calculate start and end intersection/overlap points between the
        // edge parameter and the current edge instance as a fractional length
        // along the current edge with respect to the start vertex.
        const [startFraction, endFraction] = [
            startFactor * startEdge.magnitude / this.magnitude,
            endFactor * endEdge.magnitude / this.magnitude
        ]

        return { start: startFraction, end: endFraction }
    }

    /**
     * Calculate the length of the overlapping section between two *parallel*
     * and *colinear* edges.
     * @param {Edge} edge Parallel edge to find length of overlapping section
     * with current edge instance.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.requireColinear] Should target edge be colinear to
     * current edge instance? If not, returned magnitude will be any region of
     * two edges which are perpendicularly parallel.
     * @returns {number} Magnitude of overlapping region of both edge line
     * segments.
     */
    getParallelIntersectionMagnitude(edge, { requireColinear = true } = {}) {
        // If colinear constraint not required and edges are already parallel,
        // return recursive call on edge which has been translated to be
        // colinear with the current instance.
        if (!requireColinear && this.isParallelTo(edge)) {
            // Fetch intersect between the start bisector of the current edge
            // and the target edge.
            const bisector = Line.from(this).getBisector(this.start)
            const intersect = Line.from(edge).getIntersection(bisector)
            if (!intersect) { return 0 }

            // Translate target edge by the vector between the calculated
            // intersect and the start of the current edge such that the edges
            // are colinear.
            const { x, y } = edge.start
            const { x: deltaX, y: deltaY } = this.start.subtract(intersect)
            const translatedEdge = edge
                .copy()
                .set({ start: new Vector2D(x + deltaX, y + deltaY) })

            return this.getParallelIntersectionMagnitude(translatedEdge)
        }

        const parallelIntersectionFractions = this
            .getParallelIntersectionFractions(edge)
        if (!parallelIntersectionFractions) { return 0 }

        const { start, end } = parallelIntersectionFractions
        return start < 1 && end > 0
            ? this.magnitude * (Math.min(1, end) - Math.max(0, start))
            : 0
    }

    /**
     * Calculate point of intersection between the current edge instance and
     * a vector instance as a fraction along current edge from start vertex.
     * @param {Vector2D} vector Vector to find intersection with current edge
     * instance.
     * @returns {?number} Intersection point between edge and vector as given by
     * a decimal between 0 and 1 representing a point which is a fraction along
     * current edge from start vertex. If current edge does not intersect the
     * vector, method will return `null` value.
     */
    getVectorFraction(vector) {
        // Ignore if vector is not on current edge instance.
        if (!this.intersects(vector)) { return null }

        return vector.subtract(this.#start).magnitude / this.magnitude
    }

    /**
     * Calculate shortest distance from a given point to *any* point on the
     * current edge instance. Where the point is with the "bounds" of the start
     * and end vertices of the edge, this is the perpendicular distance to line.
     * Otherwise this is the shortest of the distances from the given point to
     * the start or end vertices of the edge.
     * @param {Vector2D} vector Vector point to find distance to edge.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.forcePerpendicular] Should method return
     * perpendicular distance to point from current edge instance? If `true` and
     * line to point from edge is *not* perpendicular to edge, return `NaN`.
     * Defaults to `false`.
     * @returns {number} Shortest distance from point to any point on current
     * edge instance.
     */
    getDistanceToPoint(vector, { forcePerpendicular = false } = {}) {
        // Calculate the perpendicular distance to the extended line of the
        // current edge instance by calculating the dot product as described
        // [here](https://brilliant.org/wiki/dot-product-distance-between-point-and-a-line/)
        const {
            gradient,
            yIntercept: { y: yIntercept },
            xIntercept: { x: xIntercept }
        } = this
        let distance = !isFinite(gradient) ? Math.abs(xIntercept - vector.x)
            : gradient === 0 ? Math.abs(yIntercept - vector.y)
            : Math.abs(vector.y - gradient * vector.x - yIntercept) /
                Math.sqrt(gradient * gradient + 1)
        if (distance === 0) { return distance }

        // Copy and rotate current edge instance and target vector to be
        // vertically grid aligned in order to check if target vector is within
        // the perpendicular "bounds" of the start and end vertices of the
        // current edge instance.
        const angleToVertical = Math.PI / 2 - this.absoluteDirection
        const verticalEdge = this.rotate(angleToVertical)
        const verticalVector = vector.rotate(angleToVertical)

        // Generate edges from rotated target vector to the start and end
        // vertices of the rotated edge instance.
        const toStart = new Edge(verticalVector, verticalEdge.start)
        const toEnd = new Edge(verticalVector, verticalEdge.end)

        // Fetch gradients to start and end vectors of the vertical edge,
        // flipping values as required such that the gradients are in numerical
        // order.
        let { gradient: toStartGradient } = toStart
        let { gradient: toEndGradient } = toEnd
        if (toStartGradient > toEndGradient) {
            ({ toStartGradient, toEndGradient } = {
                toStartGradient: toEndGradient,
                toEndGradient: toStartGradient
            })
        }

        // If the toStartGradient and toEndGradient are not *above* and *below*
        // 0 respectively, the point is not within perpendicular "bounds" of the
        // start and end vertices of the current edge instance (i.e. the given
        // point is not *between* the lines extending from the start and end
        // vertices perpendicular to the current edge instance). Update distance
        // to the minimum of the distances from the requested point to the start
        // or end vertex of the current edge instance in order to account for
        // this.
        if (toStartGradient > 0 || toEndGradient < 0) {
            // Return `NaN` if a perpendicular distance from current edge
            // instance to target vector is required.
            if (forcePerpendicular) { return NaN }

            distance = Math.min(toStart.magnitude, toEnd.magnitude)
        }

        return distance
    }

    /**
     * Calculate shortest distance from *any* point on a given edge to *any*
     * other point on the current edge instance using the distance to point
     * method.
     * @param {Edge} edge Second edge to find distance to current edge instance.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.forcePerpendicular] Should method return
     * perpendicular distance to edge from current edge instance? If `true` and
     * line to edge from current edge instance is *not* perpendicular to current
     * edge instance, return `NaN`. Defaults to `false`.
     * @param {boolean} [obj.allowOpposite] Should all possible distances
     * between edges be considered? If set to `false`, only the distances from
     * the *current* edge instance to the *target* edge instance will be
     * calculated. Defaults to `true`.
     * @returns {number} Shortest distance from an edge to any point on current
     * edge instance.
     */
    getDistanceToEdge(edge, {
        forcePerpendicular = false,
        allowOpposite = true
    } = {}) {
        // Fetch distances from *current* edge instance to the start and end
        // vertices of the *second* edge instance.
        const distances = [
            this.getDistanceToPoint(edge.start, { forcePerpendicular }),
            this.getDistanceToPoint(edge.end, { forcePerpendicular })
        ]

        // If allowOpposite flag set, add perpendicular distances *from* the
        // start and end vertices of the current edge instance *to* a
        // corresponding point on the target edge which is *perpendicular* to
        // the start/end vertex relative to the current edge instance. Note
        // that, since edge instances are segments of *straight* lines, the
        // "opposite" distances calculated below, combined with the distances
        // defined above (4 distances in total), bound the absolute min and max
        // distances from *any* point on one edge to *any* point on the other.
        if (allowOpposite) {
            // Fetch line instances from current and target edge instances.
            const instanceLine = Line.from(this)
            const edgeLine = Line.from(edge)

            // Get perpendicular bisectors passing through start and end
            // vertices of current edge instance.
            const startBisector = instanceLine.getBisector(this.start)
            const endBisector = instanceLine.getBisector(this.end)

            // Get opposite perpendicular intersection points.
            const oppositeStart = edgeLine.getIntersection(startBisector)
            const oppositeEnd = edgeLine.getIntersection(endBisector)

            // Check if opposite intersection point is intersected by target
            // edge segment, or if intersection point is only on extended line.
            // Note that we cannot simply test this using the edge `intersects`
            // method, as it will not always return the correct value if the
            // calculated intersection point is slightly off the edge due to
            // floating point arithmetic errors. As such, we check for
            // intersection by creating an extended bisector edge *through* the
            // candidate intersection point.
            let edgeIntersectsOppositeStart, edgeIntersectsOppositeEnd
            if (oppositeStart) {
                const toOppositeStart = new Edge(this.start, oppositeStart)
                toOppositeStart.scalarMultiply(2, { inPlace: true })
                edgeIntersectsOppositeStart = edge.intersects(toOppositeStart)
            }
            if (oppositeEnd) {
                const toOppositeEnd = new Edge(this.end, oppositeEnd)
                toOppositeEnd.scalarMultiply(2, { inPlace: true })
                edgeIntersectsOppositeEnd = edge.intersects(toOppositeEnd)
            }

            // If opposite intersection point lies on edge segment, add distance
            // to distances array. Note that we know these points are
            // perpendicular to current edge instance, and therefore do not need
            // to set forcePerpendicular flag.
            if (oppositeStart && edgeIntersectsOppositeStart) {
                distances.push(this.getDistanceToPoint(oppositeStart))
            }
            if (oppositeEnd && edgeIntersectsOppositeEnd) {
                distances.push(this.getDistanceToPoint(oppositeEnd))
            }
        }

        // Filter out any `NaN` undefined values in distances array.
        const definedDistances = distances.filter(distance => !isNaN(distance))

        return definedDistances.length ? Math.min(...definedDistances) : NaN
    }

    /**
     * Check if line segments intersect based on the orientation (clockwise,
     * anticlockwise or colinear) of start and end vertices of the two
     * edges/line segments. Note that:
     * - Given any arrangement of two line segments, the orientation of various
     * groups of vertices may be used to determine if the segment intersects.
     * - See [here](https://www.geeksforgeeks.org/check-if-two-given-line-segments-intersect/)
     * for more information on the details of the standard intersect algorithm,
     * and for diagrams which better illustrate the idea of the algorithm.
     * @param {Edge} edge Edge instance argument to check for intersection.
     * @returns {boolean} Does edge argument intersect the edge instance?
     */
    #intersectsEdge(edge) {
        // Check if any start or end vertex of one edge lies on the *other*
        // edge. In this case the orientation of the points will be collinear,
        // and we can skip other orientation checks, as the edges obviously
        // intersect.
        if (
            this.#intersectsVector(edge.start) ||
            this.#intersectsVector(edge.end) ||
            edge.#intersectsVector(this.start) ||
            edge.#intersectsVector(this.end)
        ) { return true }

        // Fetch orientation triples required for the line segment intersection
        // check.
        const vertexOrientations = {
            _ABC: Vector2D.orientation(this.start, this.end, edge.start),
            _ABD: Vector2D.orientation(this.start, this.end, edge.end),
            _ACD: Vector2D.orientation(this.start, edge.start, edge.end),
            _BCD: Vector2D.orientation(this.end, edge.start, edge.end)
        }

        // Check intersection based on orientation of points as calculated
        // above.
        if (
            vertexOrientations._ABC !== vertexOrientations._ABD &&
            vertexOrientations._ACD !== vertexOrientations._BCD
        ) { return true }

        return false
    }

    /**
     * Check if current edge instance intersects a given vector based on the
     * orientation (clockwise, anticlockwise or colinear) of the start and end
     * vertices of the current edge instance and the given 3rd vector point, and
     * the position of the vector with respect to the edge.
     * @param {Vector2D} vector Vector instance to test for intersection with
     * current edge instance.
     * @returns {boolean} Does current edge instance intersect vector?
     */
    #intersectsVector(vector) {
        // If points are not colinear, return false since vector *cannot* be on
        // the extended line of the current edge instance.
        if (Vector2D.orientation(this.start, this.end, vector) !== 0) {
            return false
        }

        // Return boolean based on if vector is within current edge instance
        // start and end vertices, or on extended line of edge.
        return vector.x >= Math.min(this.start.x, this.end.x) &&
            vector.y >= Math.min(this.start.y, this.end.y) &&
            vector.x <= Math.max(this.start.x, this.end.x) &&
            vector.y <= Math.max(this.start.y, this.end.y)
    }

    /**
     * Fixed reference start vertex of edge.
     */
    get start() {
        return this.#start
    }

    /**
     * Dynamically generated end vertex of edge.
     */
    get end() {
        return this.start.add(this)
    }

    get gradient() {
        return this.y / this.x
    }

    /**
     * Dynamically generated x-intercept vector of edge.
     */
    get xIntercept() {
        const intercept = this.gradient !== 0
            ? this.#start.x - this.#start.y * (1 / this.gradient)
            : NaN

        return new Vector2D(intercept, 0)
    }

    /**
     * Dynamically generated y-intercept vector of edge.
     */
    get yIntercept() {
        const intercept = isFinite(this.gradient)
            ? this.#start.y - this.#start.x * this.gradient
            : NaN

        return new Vector2D(0, intercept)
    }
}

/**
 * Class implementing line from gradient and axis intercepts providing:
 * - Intercept and gradient properties.
 * - Coordinate calculation of point on line at a given opposite coordinate.
 * - Method to generate perpendicular bisector line instances.
 * - Intersection calculation between line instances.
 * - Static method to generate extended line instance from edge line segment.
 */
class Line {
    /**
     * Construct line instance from gradient and sufficient intercept properties
     * to uniquely define a straight line. Note that any supplied intercepts
     * must *not* conflict.
     * @param {number} gradient Gradient of line instance.
     * @param {object} obj Optional destructured parameters object.
     * @param {number} [obj.yIntercept] Y intercept of line instance. Strictly
     * required if gradient is `0`, not required if gradient is `Infinity`.
     * @param {number} [obj.xIntercept] X intercept of line instance. Strictly
     * required if gradient is `Infinity`, not required if gradient is `0`.
     * @param {number} [obj.conflictTolerance] Acceptable tolerance between the
     * expected value of the `y` intercept inferred from the given value of the
     * `x` intercept, and the actual given value of the `y` intercept. If both
     * intercepts are defined, and the expected vs given values differ by more
     * than this tolerance, the constructor will throw. Tolerance required due
     * to floating point arithmetic errors.
     * @throws {LineError} Throws when:
     * - Given intercepts conflict more than the accepted tolerance based on
     * the line gradient.
     * - Given intercepts do not uniquely identify a straight line, for example
     * if no intercepts are provided.
     */
    constructor(gradient, {
        yIntercept = NaN,
        xIntercept = NaN,
        conflictTolerance = 1e-10
    } = {}) {
        // Throw if given intercepts conflict with each other based on line
        // gradient. Note that conflict tolerance is required due to floating
        // point arithmetic errors.
        const interceptsConflict = isFinite(xIntercept) &&
            isFinite(yIntercept) &&
            - xIntercept * gradient - yIntercept > conflictTolerance
        if (interceptsConflict) {
            throw new LineError({
                message: "Given intercepts conflict based on line gradient",
                fix: "Provide correct intercepts"
            })
        }

        // Throw if given intercepts do not uniquely identify a straight line.
        if (
            (isNaN(xIntercept) && isNaN(yIntercept)) ||
            (!isFinite(gradient) && isNaN(xIntercept)) ||
            (gradient === 0 && isNaN(yIntercept))
        ) {
            throw new LineError({
                message: "Given intercepts do not uniquely identify a line",
                fix: "Provide correct intercepts"
            })
        }

        // Set line gradient, ensuring that infinite gradients are set to
        // `Infinity` and not `-Infinity`.
        this.gradient = isFinite(gradient) ? gradient : Infinity

        // Set `x` or `y` intercept depending on which one is defined, and
        // infer the opposite axis intercept from that value. Note that:
        // - If the `x` intercept is defined, gradient *cannot* be `0`, so this
        // case does not need to be considered.
        // - If the `y` intercept is defined, gradient *cannot* be `Infinity`,
        // so this case does not need to be considered.
        if (isFinite(xIntercept) && isFinite(yIntercept)) {
            this.xIntercept = xIntercept
            this.yIntercept = yIntercept
        }
        else if (isFinite(xIntercept)) {
            this.xIntercept = xIntercept
            this.yIntercept = !isFinite(this.gradient) ? NaN
                : - this.xIntercept * this.gradient
        }
        else if (isFinite(yIntercept)) {
            this.yIntercept = yIntercept
            this.xIntercept = this.gradient === 0 ? NaN
                : - this.yIntercept / this.gradient
        }
        this.xIntercept ??= NaN
        this.yIntercept ??= NaN
    }

    /**
     * Get `y` coordinate of point on current line instance at a given `x`
     * coordinate. Not that if gradient of current line instance is `Infinity`,
     * `y` coordinate will not be defined, and `NaN` will be returned.
     * @param {number} x Requested `x` coordinate to get `y` coordinate.
     * @returns {number} Calculated `y` coordinate of point at given `x`
     * coordinate.
     */
    getY(x) {
        return !isFinite(this.gradient) ? NaN
            : this.gradient === 0 ? this.yIntercept
            : this.gradient * x + this.yIntercept
    }

    /**
     * Get `x` coordinate of point on current line instance at a given `y`
     * coordinate. Not that if gradient of current line instance is `0`, `x`
     * coordinate will not be defined, and `NaN` will be returned.
     * @param {number} y Requested `y` coordinate to get `x` coordinate.
     * @returns {number} Calculated `x` coordinate of point at given `y`
     * coordinate.
     */
    getX(y) {
        return !isFinite(this.gradient) ? this.xIntercept
            : this.gradient === 0 ? NaN
            : (y - this.yIntercept) / this.gradient
    }

    /**
     * Generate a perpendicular bisector of the current line instance which
     * passes through a given point vector.
     * @param {Vector2D} vector Point vector which the perpendicular bisector
     * must intersect.
     * @returns {Line} Perpendicular bisector intersecting given point vector.
     */
    getBisector(vector) {
        // Calculate gradient of bisector, accounting for 0 or infinite gradient
        // instances.
        const gradient = !isFinite(this.gradient) ? 0
            : this.gradient === 0 ? Infinity
            : - 1 / this.gradient

        // Calculate x and y intercept coordinates, accounting for 0 or
        // infinite gradient as calculated above.
        const yIntercept = isFinite(gradient)
            ? vector.y - vector.x * gradient
            : NaN
        const xIntercept = gradient !== 0
            ? vector.x - vector.y * (1 / gradient)
            : NaN

        return new Line(gradient, { yIntercept, xIntercept })
    }

    /**
     * Get intersection point vector between current line instance and a second
     * line instance.
     * @param {Line} line Second line instance to calculate intersection with
     * current instance.
     * @returns {?Vector2D} Intersection point vector, or `null` if line
     * instances are parallel to each other.
     */
    getIntersection(line) {
        // Ignore if line instances are parallel.
        if (this.gradient === line.gradient) { return null }

        // Calculate change in gradient and intercepts between line instances
        // required to calculate coordinate of intersection.
        const deltaIntercept = line.yIntercept - this.yIntercept
        const deltaGradient = this.gradient - line.gradient

        // Calculate coordinates of intersection, accounting for 0 or infinite
        // gradients as required. Note that:
        // - The `x` coordinate is inferred from equating and rearranging the
        // linear equation (y = mx + c) of each line.
        // - The `y` coordinate is inferred from substitution.
        const x = isFinite(deltaGradient) ? deltaIntercept / deltaGradient
            : !isFinite(this.gradient) ? this.xIntercept
            : line.xIntercept
        const y = isFinite(this.gradient)
            ? this.gradient * x + this.yIntercept
            : line.gradient * x + line.yIntercept

        return new Vector2D(x, y)
    }

    /**
     * Generate an extended line instance from an edge line segment.
     * @param {Edge} edge Edge instance to generate extended line instances.
     * @returns {Line} Line instance generated from given edge instance.
     */
    static from(edge) {
        // Fetch required properties from edge instance to generate line.
        const { gradient, yIntercept: { y }, xIntercept: { x } } = edge

        return new Line(gradient, { yIntercept: y, xIntercept: x })
    }
}

/**
 * Named line error.
 */
class LineError extends DecoratedError {
    /**
     * Construct linked list node named error.
     * @param {Object.<string, string|number|boolean>} options Options object
     * containing any string keys, and string, number or boolean values.
     */
    constructor(options) {
        super(options)
        this.name ??= "LineError"
    }
}

// @@exports
export { Edge, Line }
